<template>
  <main class="login-shell">
    <section class="login-panel">
      <img src="@/assets/logo.svg" alt="Orator Pro" class="brand-logo" />
      <h1 class="login-title">Sign in</h1>
      <p class="login-copy">Use your Orator Pro account to access event tools.</p>

      <form class="login-form" @submit.prevent="submitLogin">
        <div>
          <label class="field-label">Email</label>
          <input v-model.trim="email" type="email" class="field-input" autocomplete="email" required />
        </div>
        <div>
          <label class="field-label">Password</label>
          <input v-model="password" type="password" class="field-input" autocomplete="current-password" required />
        </div>
        <p v-if="error" class="notice error">{{ error }}</p>
        <button class="primary-button" type="submit" :disabled="loading">
          {{ loading ? "Signing in..." : "Sign in" }}
        </button>
      </form>
    </section>
  </main>
</template>

<script>
import { fetchEvents, login } from "@/lib/api";
import { setSession } from "@/lib/auth";

export default {
  name: "LoginView",
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      error: "",
    };
  },
  methods: {
    async submitLogin() {
      this.loading = true;
      this.error = "";
      try {
        const session = await login(this.email, this.password);
        setSession(session);
        this.$router.push(this.$route.query.redirect || await this.defaultPostLoginPath(session));
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to sign in.";
      } finally {
        this.loading = false;
      }
    },
    async defaultPostLoginPath(session) {
      const memberships = session?.memberships || [];
      const roles = new Set(memberships.map((membership) => membership.role));
      const isOratorOnly = roles.size === 1 && roles.has("orator");
      if (!isOratorOnly) {
        return "/";
      }

      try {
        const events = await fetchEvents();
        if (events.length === 1 && events[0]?.eventId) {
          return `/events/${encodeURIComponent(events[0].eventId)}/presenter`;
        }
      } catch (error) {
        return "/";
      }
      return "/";
    },
  },
};
</script>

<style scoped>
.login-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--orator-light);
  padding: 1rem;
}

.login-panel {
  width: min(100%, 420px);
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgb(15 23 42 / 0.08);
  padding: 2rem;
}

.brand-logo {
  width: 180px;
  height: auto;
}

.login-title {
  margin-top: 2rem;
  font-size: 2rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.login-copy {
  margin-top: 0.35rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.login-form {
  margin-top: 1.5rem;
  display: grid;
  gap: 1rem;
}

.field-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--orator-arsenic);
}

.field-input {
  width: 100%;
  border: 1px solid var(--orator-steel);
  border-radius: 0.375rem;
  padding: 0.7rem 0.8rem;
}

.primary-button {
  background: var(--orator-navy);
  color: white;
  border: 0;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  font-weight: 800;
}

.primary-button:disabled {
  opacity: 0.55;
}

.notice.error {
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.75rem;
}
</style>
